import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileText, Download, Calendar, TrendingUp } from 'lucide-react';
import { toast } from "sonner";
import { isNativePlatform, openBlobFileOnNative } from '@/utils/nativeAndroidFiles';

const TEAM_CONFIG = {
  worship: {
    label: 'Louvor',
    memberEntity: 'WorshipTeamMember',
    scheduleEntity: 'WorshipSchedule',
  },
  media: {
    label: 'Mídia',
    memberEntity: 'MediaTeamMember',
    scheduleEntity: 'MediaSchedule',
  },
  dance: {
    label: 'Dança MBV',
    memberEntity: 'DanceTeamMember',
    scheduleEntity: 'DanceSchedule',
  },
  reception: {
    label: 'Recepção',
    memberEntity: 'ReceptionTeamMember',
    scheduleEntity: 'ReceptionSchedule',
  },
  kids: {
    label: 'Kids MBV',
    memberEntity: 'KidsMBVMember',
    scheduleEntity: 'KidsMBVSchedule',
  },
};

const TEAM_OPTIONS = ['worship', 'media', 'dance', 'reception', 'kids'];

function parseDateOnly(value) {
  const normalized = String(value || '').slice(0, 10);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateLabel(value) {
  const date = parseDateOnly(value);
  if (!date) return 'Sem data';
  return date.toLocaleDateString('pt-BR');
}

function inDateRange(value, startDate, endDate) {
  const current = parseDateOnly(value);
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!current || !start || !end) return false;
  return current >= start && current <= end;
}

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeIdentity(value) {
  return normalizeText(value).toLowerCase();
}

function resolveRequestedTeams(teamType) {
  return teamType === 'all' ? TEAM_OPTIONS : [teamType];
}

function buildMemberKey(member) {
  return member.member_id || normalizeIdentity(member.email) || normalizeIdentity(member.full_name) || member.id;
}

function matchScheduleMember(member, scheduleMember) {
  if (!member || !scheduleMember) return false;

  const memberId = member.member_id || member.id || null;
  const scheduleMemberId = scheduleMember.member_id || null;
  if (memberId && scheduleMemberId && memberId === scheduleMemberId) return true;

  const memberEmail = normalizeIdentity(member.email);
  const scheduleMemberEmail = normalizeIdentity(scheduleMember.member_email);
  if (memberEmail && scheduleMemberEmail && memberEmail === scheduleMemberEmail) return true;

  const memberName = normalizeIdentity(member.full_name || member.member_name);
  const scheduleMemberName = normalizeIdentity(scheduleMember.name || scheduleMember.member_name);
  return !!memberName && memberName === scheduleMemberName;
}

async function fetchReportData(selectedTeams, startDate, endDate) {
  const entries = await Promise.all(
    selectedTeams.map(async (teamKey) => {
      const config = TEAM_CONFIG[teamKey];
      const [members, schedules] = await Promise.all([
        base44.entities[config.memberEntity].list('-created_date'),
        base44.entities[config.scheduleEntity].list('-date'),
      ]);

      return {
        teamKey,
        teamLabel: config.label,
        members: members || [],
        schedules: (schedules || []).filter((schedule) => inDateRange(schedule.date, startDate, endDate)),
      };
    })
  );

  return entries;
}

function buildParticipationRows(reportData) {
  return reportData.flatMap(({ teamLabel, members, schedules }) =>
    members
      .map((member) => {
        const memberSchedules = schedules.filter((schedule) =>
          (schedule.members || []).some((scheduleMember) => matchScheduleMember(member, scheduleMember))
        );
        const confirmedCount = memberSchedules.reduce((total, schedule) => {
          const found = (schedule.members || []).find((scheduleMember) => matchScheduleMember(member, scheduleMember));
          return total + (found?.confirmed ? 1 : 0);
        }, 0);

        const totalSchedules = memberSchedules.length;
        const confirmationRate = totalSchedules > 0 ? Math.round((confirmedCount / totalSchedules) * 100) : 0;

        return {
          Equipe: teamLabel,
          Integrante: member.full_name || member.member_name || 'Sem nome',
          Email: member.email || '-',
          Telefone: member.phone || '-',
          Escalas: totalSchedules,
          Confirmados: confirmedCount,
          'Taxa %': `${confirmationRate}%`,
        };
      })
      .filter((row) => row.Escalas > 0)
  );
}

function buildScheduleRows(reportData) {
  return reportData.flatMap(({ teamLabel, schedules }) =>
    schedules.map((schedule) => {
      const totalMembers = schedule.members?.length || 0;
      const confirmedCount = schedule.members?.filter((member) => member.confirmed).length || 0;
      const confirmationRate = totalMembers > 0 ? Math.round((confirmedCount / totalMembers) * 100) : 0;
      const date = parseDateOnly(schedule.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let status = 'Futura';
      if (date && date < today) {
        status = confirmationRate === 100 ? 'Concluída' : 'Realizada';
      } else if (confirmationRate > 0) {
        status = 'Em confirmação';
      }

      return {
        Equipe: teamLabel,
        Escala: schedule.title || 'Sem título',
        Data: formatDateLabel(schedule.date),
        Hora: schedule.start_time || '-',
        Local: schedule.location || '-',
        Integrantes: totalMembers,
        Confirmados: confirmedCount,
        'Taxa %': `${confirmationRate}%`,
        Status: status,
      };
    })
  );
}

function buildConfirmationRows(reportData) {
  return reportData.flatMap(({ teamLabel, schedules }) =>
    schedules.map((schedule) => {
      const totalMembers = schedule.members?.length || 0;
      const confirmedCount = schedule.members?.filter((member) => member.confirmed).length || 0;
      const pendingCount = Math.max(totalMembers - confirmedCount, 0);
      const confirmationRate = totalMembers > 0 ? Math.round((confirmedCount / totalMembers) * 100) : 0;

      return {
        Equipe: teamLabel,
        Escala: schedule.title || 'Sem título',
        Data: formatDateLabel(schedule.date),
        Confirmados: confirmedCount,
        Pendentes: pendingCount,
        Total: totalMembers,
        'Taxa %': `${confirmationRate}%`,
      };
    })
  );
}

function buildOverview(reportData) {
  const totalSchedules = reportData.reduce((sum, item) => sum + item.schedules.length, 0);
  const totalMembers = reportData.reduce((sum, item) => sum + item.members.length, 0);
  const totalAssignments = reportData.reduce(
    (sum, item) => sum + item.schedules.reduce((inner, schedule) => inner + (schedule.members?.length || 0), 0),
    0
  );
  const totalConfirmed = reportData.reduce(
    (sum, item) =>
      sum +
      item.schedules.reduce(
        (inner, schedule) => inner + (schedule.members?.filter((member) => member.confirmed).length || 0),
        0
      ),
    0
  );

  const averageConfirmationRate = totalAssignments > 0 ? Math.round((totalConfirmed / totalAssignments) * 100) : 0;

  return {
    teams: reportData.map((item) => item.teamLabel).join(', '),
    totalSchedules,
    totalMembers,
    totalAssignments,
    totalConfirmed,
    averageConfirmationRate,
  };
}

function buildReportSections(reportType, reportData) {
  const overview = buildOverview(reportData);
  const sections = [
    {
      title: 'Resumo Geral',
      columns: ['Indicador', 'Valor'],
      rows: [
        { Indicador: 'Equipes', Valor: overview.teams || 'Nenhuma' },
        { Indicador: 'Escalas no período', Valor: overview.totalSchedules },
        { Indicador: 'Integrantes cadastrados', Valor: overview.totalMembers },
        { Indicador: 'Participações nas escalas', Valor: overview.totalAssignments },
        { Indicador: 'Confirmações registradas', Valor: overview.totalConfirmed },
        { Indicador: 'Média de confirmação', Valor: `${overview.averageConfirmationRate}%` },
      ],
    },
  ];

  if (reportType === 'participation' || reportType === 'complete') {
    sections.push({
      title: 'Participação dos Integrantes',
      columns: ['Equipe', 'Integrante', 'Email', 'Telefone', 'Escalas', 'Confirmados', 'Taxa %'],
      rows: buildParticipationRows(reportData),
    });
  }

  if (reportType === 'schedules' || reportType === 'complete') {
    sections.push({
      title: 'Escalas do Período',
      columns: ['Equipe', 'Escala', 'Data', 'Hora', 'Local', 'Integrantes', 'Confirmados', 'Taxa %', 'Status'],
      rows: buildScheduleRows(reportData),
    });
  }

  if (reportType === 'confirmation' || reportType === 'complete') {
    sections.push({
      title: 'Taxa de Confirmação',
      columns: ['Equipe', 'Escala', 'Data', 'Confirmados', 'Pendentes', 'Total', 'Taxa %'],
      rows: buildConfirmationRows(reportData),
    });
  }

  return sections.map((section) => ({
    ...section,
    rows: section.rows.length ? section.rows : [{ Informação: 'Nenhum dado encontrado no período selecionado.' }],
    columns: section.rows.length ? section.columns : ['Informação'],
  }));
}

function escapeCsv(value) {
  const normalized = String(value ?? '');
  if (/[",;\n]/.test(normalized)) {
    return `"${normalized.replaceAll('"', '""')}"`;
  }
  return normalized;
}

function buildCsvContent(reportTitle, sections) {
  const lines = [reportTitle, ''];

  sections.forEach((section, index) => {
    lines.push(section.title);
    lines.push(section.columns.join(';'));
    section.rows.forEach((row) => {
      lines.push(section.columns.map((column) => escapeCsv(row[column] ?? '')).join(';'));
    });
    if (index < sections.length - 1) {
      lines.push('');
    }
  });

  return lines.join('\n');
}

function buildPdfBlob(reportTitle, periodLabel, sections) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const lineHeight = 5.5;
  let cursorY = 16;

  const ensureSpace = (needed = 10) => {
    if (cursorY + needed <= pageHeight - margin) return;
    pdf.addPage();
    cursorY = 16;
  };

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(reportTitle, margin, cursorY);
  cursorY += 7;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(periodLabel, margin, cursorY);
  cursorY += 8;

  sections.forEach((section) => {
    ensureSpace(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(section.title, margin, cursorY);
    cursorY += 6;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');

    section.rows.forEach((row) => {
      const content = section.columns
        .map((column) => `${column}: ${String(row[column] ?? '-')}`)
        .join(' | ');
      const wrapped = pdf.splitTextToSize(content, pageWidth - margin * 2);
      ensureSpace(wrapped.length * lineHeight + 3);
      pdf.text(wrapped, margin, cursorY);
      cursorY += wrapped.length * lineHeight + 1.5;
    });

    cursorY += 3;
  });

  return pdf.output('blob');
}

async function downloadReportBlob(blob, filename) {
  if (isNativePlatform()) {
    await openBlobFileOnNative(blob, filename, blob.type || 'application/octet-stream', {
      title: filename,
      text: 'Relatório gerado com sucesso.',
    });
    return;
  }

  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  anchor.remove();
}

export default function Relatorios() {
  const [reportType, setReportType] = useState('participation');
  const [teamType, setTeamType] = useState('worship');
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('pdf');
  const [generating, setGenerating] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Por favor, selecione o período do relatório');
      return;
    }

    if (startDate > endDate) {
      toast.error('A data inicial não pode ser maior que a data final.');
      return;
    }

    setGenerating(true);
    try {
      const selectedTeams = resolveRequestedTeams(teamType);
      const reportData = await fetchReportData(selectedTeams, startDate, endDate);
      const sections = buildReportSections(reportType, reportData);
      const reportTitle = `Relatório ${reportType === 'complete' ? 'Completo' : 'de Ministério'}`;
      const periodLabel = `Período: ${formatDateLabel(startDate)} até ${formatDateLabel(endDate)}`;
      const safePeriod = `${startDate}_${endDate}`;
      const safeTeam = teamType === 'all' ? 'todas-equipes' : teamType;
      const filenameBase = `relatorio-${reportType}-${safeTeam}-${safePeriod}`;

      if (format === 'csv') {
        const csvContent = buildCsvContent(reportTitle, sections);
        const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        await downloadReportBlob(csvBlob, `${filenameBase}.csv`);
      } else {
        const pdfBlob = buildPdfBlob(reportTitle, periodLabel, sections);
        await downloadReportBlob(pdfBlob, `${filenameBase}.pdf`);
      }

      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setGenerating(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <PageHeader title="Relatórios" showBack={true} />
        <div className="max-w-lg mx-auto mt-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-gray-600">
                Apenas administradores podem acessar os relatórios.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader 
            title="Relatórios" 
            subtitle="Análise de escalas e participação"
            showBack={true}
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                Escalas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">Período</p>
              <p className="text-xs text-gray-500 mt-1">Filtre por data e equipe</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                Participação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">Dados reais</p>
              <p className="text-xs text-gray-500 mt-1">Escalas e confirmações</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Exportação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">PDF / CSV</p>
              <p className="text-xs text-gray-500 mt-1">Baixe no site ou app</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Gerar Relatório</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Relatório</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participation">Participação dos Integrantes</SelectItem>
                    <SelectItem value="schedules">Escalas Concluídas/Pendentes</SelectItem>
                    <SelectItem value="confirmation">Taxa de Confirmação</SelectItem>
                    <SelectItem value="complete">Relatório Completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Equipe</Label>
                <Select value={teamType} onValueChange={setTeamType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worship">Louvor</SelectItem>
                    <SelectItem value="media">Mídia</SelectItem>
                    <SelectItem value="dance">Dança MBV</SelectItem>
                    <SelectItem value="reception">Recepção</SelectItem>
                    <SelectItem value="kids">Kids MBV</SelectItem>
                    <SelectItem value="all">Todas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Período</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Formato</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="pt-4">
              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleGenerateReport}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <LoadingSpinner />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Gerar e Baixar Relatório
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Informações sobre Relatórios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Participação dos Integrantes</p>
                <p>Lista detalhada de cada integrante com número de escalas, confirmações e taxa de participação.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Escalas Concluídas/Pendentes</p>
                <p>Resumo de todas as escalas do período, separadas por status e equipe.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Taxa de Confirmação</p>
                <p>Análise de confirmações de presença por escala e pendências restantes.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Relatório Completo</p>
                <p>Combina resumo geral, participação, escalas e confirmações em um único arquivo.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
